/**
 * @file Shared React component — components/DataVisualisation/CementBoxPlotChart.tsx.
 */

// src/components/DataVisualisation/CementBoxPlotChart.tsx
// Pixel-perfect Box Plot (Whiskers Plot) matching the exact screenshot design
// Uses pure SVG for precise control over every visual element

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Customized,
  type RectangleProps,
} from "recharts";
import "./CementBoxPlotChart.css";
import {
  bucketForTime,
  datasetTimeMs,
  expandTenYearSlicerBuckets,
  type TimeSlicer,
} from "./boxPlotCommon";

/** Type definition — Cement Box Plot Dataset. */
export interface CementBoxPlotDataset {
  id: string;
  submissionCode?: string | null;
  submission_code?: string | null;
  title?: string | null;
  createdAt?: string | null;
  /** Used for timeline bucketing before createdAt (publication Y-M-D or EPD date). */
  publication?: {
    publicationYear?: string | null;
    publicationMonth?: string | null;
    publicationDay?: string | null;
    epdPublicationYear?: string | null;
    epdPublicationMonth?: string | null;
    epdPublicationDay?: string | null;
    year?: string | null;
  } | null;
  background?: {
    materialId?: number | null;
    material_id?: number | null;
    materialName?: string | null;
    material_name?: string | null;
    subMaterialId?: number | null;
    subMaterialIdName?: string | null;
    sub_material_id_name?: string | null;
    subMaterialName?: string | null;
    sub_material_name?: string | null;
    subMaterialUnit?: string | null;
    sub_material_unit?: string | null;
    technologyId?: number | null;
    technologyIdName?: string | null;
    materialCode?: string | null;
    material_code?: string | null;
    material?: { name?: string | null; code?: string | null } | null;
    subMaterial?: { name?: string | null; code?: string | null } | null;
    technology?: { name?: string | null } | null;
    manufacturingProcess?: string | null;
    collectionStartYear?: string | null;
    collectionEndYear?: string | null;
  } | null;
  a1Summary?: {
    embodiedEnergyValue?: number | string | null;
    embodiedCarbonValue?: number | string | null;
  } | null;
  a2Summary?: {
    embodiedEnergyValue?: number | string | null;
    embodiedCarbonValue?: number | string | null;
  } | null;
  a3Summary?: {
    embodiedEnergyValue?: number | string | null;
    embodiedCarbonValue?: number | string | null;
  } | null;
  a1a3Summary?: {
    embodiedEnergyValue?: number | string | null;
    embodiedCarbonValue?: number | string | null;
  } | null;
}

/* ══════════════════════════════════════════════════════════════════════════
   TYPE DEFINITIONS
   ══════════════════════════════════════════════════════════════════════════ */

interface BoxPlotFilters {
  /** Yes: one column group per sub-material, each with all date (timeline) buckets. No: aggregate to parent material × dates. */
  materialSubType: "Yes" | "No";
  /** Yes: split columns by manufacturing technology (nested within sub-material when both Yes). No: aggregate technologies. */
  manufacturingTechnology: "Yes" | "No";
  timeSlicer: TimeSlicer;
}

type TimelineData = Record<string, number[]>;

interface BoxStats {
  min: number;
  q1: number;
  median: number;
  q3: number;
  /** Whisker end: max inlier (values inside Q1 − 1.5×IQR … Q3 + 1.5×IQR). */
  max: number;
  /** Raw minimum across all values in the slot (including outliers). */
  dataMin: number;
  /** Raw maximum across all values in the slot (including outliers). */
  dataMax: number;
  outliers: number[];
  mean: number;
  count: number;
  hasData: boolean;
}

interface ComputedSlot {
  material: string;
  process: string;
  timeline: string;
  stats: BoxStats;
  /** Subset of values for `highlightDatasetIds` in the same slot (review queue, etc.). */
  highlightStats: BoxStats;
  /** Raw metric values in this slot (all datasets). */
  values: number[];
  /** Raw highlight-subset values in this slot. */
  highlightValues: number[];
  xCenter: number;
}

interface Props {
  datasets?: CementBoxPlotDataset[];
  /** Hide filter controls (Metric / Material Sub-type / Time slicer) */
  showFilters?: boolean;
  /** Parent material name (e.g. from Material Overview) when API omits nested material.name */
  parentMaterialName?: string | null;
  /**
   * When set (including `[]`), enables a second box plot per column from datasets whose `id` is in this list (black).
   * Omit or pass `null` to disable.
   */
  highlightDatasetIds?: readonly string[] | null;
  /** When true and highlight is enabled, show a short legend for all vs highlighted data. */
  showHighlightLegend?: boolean;
  /**
   * When set, bar/column clicks report which datasets belong to that chart slot (admin list filtering).
   * Receives the same slot grouping as the visible chart for the active metric.
   */
  onSlotClick?: (detail: CementBoxPlotSlotSelection) => void;
  /** Enables pointer cursor and click handling for chart columns (use with `onSlotClick`). */
  interactiveSlotSelection?: boolean;
  /** Called when the metric toggle changes while interactive selection is enabled (parent may clear selection). */
  onInteractiveMetricChange?: () => void;
  /** Controlled metric selection (sync with other charts). */
  metric?: CementBoxPlotMetric;
  /** Called when the metric dropdown changes. */
  onMetricChange?: (metric: CementBoxPlotMetric) => void;
}

// No hardcoded sample data: the chart is driven by API datasets.

/* ══════════════════════════════════════════════════════════════════════════
   STATISTICAL FUNCTIONS
   ══════════════════════════════════════════════════════════════════════════ */

function sortAsc(arr: number[]): number[] {
  return [...arr].sort((a, b) => a - b);
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] * (hi - idx) + sorted[hi] * (idx - lo);
}

function computeBoxStats(values: number[]): BoxStats {
  if (values.length === 0) {
    return {
      min: 0,
      q1: 0,
      median: 0,
      q3: 0,
      max: 0,
      dataMin: 0,
      dataMax: 0,
      outliers: [],
      mean: 0,
      count: 0,
      hasData: false,
    };
  }

  const sorted = sortAsc(values);
  const q1 = percentile(sorted, 0.25);
  const median = percentile(sorted, 0.5);
  const q3 = percentile(sorted, 0.75);
  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const outliers = sorted.filter((v) => v < lowerFence || v > upperFence);
  const inliers = sorted.filter((v) => v >= lowerFence && v <= upperFence);

  const mean = values.reduce((acc, v) => acc + v, 0) / values.length;

  return {
    min: inliers.length > 0 ? Math.min(...inliers) : sorted[0],
    q1,
    median,
    q3,
    max: inliers.length > 0 ? Math.max(...inliers) : sorted[sorted.length - 1],
    dataMin: sorted[0],
    dataMax: sorted[sorted.length - 1],
    outliers,
    mean,
    count: values.length,
    hasData: true,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   LAYOUT CONSTANTS - Pixel-perfect positioning
   ══════════════════════════════════════════════════════════════════════════ */

const SVG_WIDTH = 1050;
const SVG_HEIGHT = 520;
// Increased bottom margin to prevent label overlap/clipping
const MARGIN = { top: 50, right: 30, bottom: 170, left: 85 };
const PLOT_LEFT = MARGIN.left;
const PLOT_RIGHT = SVG_WIDTH - MARGIN.right;
const PLOT_TOP = MARGIN.top;
const PLOT_BOTTOM = SVG_HEIGHT - MARGIN.bottom;
const PLOT_WIDTH = PLOT_RIGHT - PLOT_LEFT;
const PLOT_HEIGHT = PLOT_BOTTOM - PLOT_TOP;

const Y_MIN = 0;

// Box styling
const BOX_FILL = "#E8ADA8";
const BOX_STROKE = "#D4847D";
const BOX_FILL_HIGHLIGHT = "#101010";
const BOX_STROKE_HIGHLIGHT = "#101010";
const WHISKER_COLOR = "#D4847D";

// Keep box-plot bar width consistent (professional look)
const BAR_SIZE_PX = 16;
// Controls horizontal density of the chart (smaller = narrower chart; wider = more room for date labels)
const SLOT_PX = 40;
// Row height for axis section labels (timeline + category rows)
const AXIS_SECTION_ROW_H = 70;
/** Maximum X-axis footer rows: Date → Manufacturing Technology → Material Sub-type → Material */
const AXIS_FOOTER_ROW_COUNT = 4;

function xAxisFooterHeightPx(rows = AXIS_FOOTER_ROW_COUNT): number {
  const rowCount = Math.max(0, rows);
  if (rowCount === 0) return 14;
  return rowCount * AXIS_SECTION_ROW_H + (rowCount - 1) * 4 + 14;
}

// Left margin defines plot "0" (y-axis line). Extra room for two-line Y-axis label (foreignObject).
const CHART_MARGIN = { top: 16, right: 24, bottom: 16, left: 56 };
const Y_AXIS_WIDTH_PX = 84;
/** Pixel offset where the plot / x-axis columns begin (left margin + y-axis width). */
const PLOT_LEFT_OFFSET_PX = CHART_MARGIN.left + Y_AXIS_WIDTH_PX;
/** Fixed height (px) for the box-plot drawing area — never changes when filters toggle. */
const BOX_PLOT_AREA_HEIGHT_PX = 340;

/** Fixed X-axis footer band — keeps Y-axis plot height constant across filter toggles. */
const FIXED_X_AXIS_FOOTER_HEIGHT_PX = xAxisFooterHeightPx(AXIS_FOOTER_ROW_COUNT);

/** Total chart height: always reserve max footer rows so the Y-axis plot area stays fixed. */
function chartTotalHeightPx(): number {
  return (
    CHART_MARGIN.top +
    CHART_MARGIN.bottom +
    FIXED_X_AXIS_FOOTER_HEIGHT_PX +
    BOX_PLOT_AREA_HEIGHT_PX
  );
}

/** Horizontal strip for Y-axis title + unit (HTML); avoids SVG rotated text clipping when plot height is short. */
const Y_AXIS_LABEL_COL_W = 78;

function makeSlotKey(material: string, process: string, timeline: string) {
  return `${material}|||${process}|||${timeline}`;
}

type BoxPlotStackDatum = {
  key: string;
  material: string;
  process: string;
  timeline: string;
  min: number;
  bottomWhisker: number;
  bottomBox: number;
  topBox: number;
  topWhisker: number;
  bar: number; // "0-height" bars used to render cap lines
  // keep for tooltip
  q1: number;
  median: number;
  q3: number;
  max: number;
  dataMin: number;
  dataMax: number;
  outliers: number[];
  /** All raw values in this slot (used to draw outlier dots). */
  values: number[];
  count: number;
};

type OutlierDot = {
  id: string;
  /** Index in the chart `data` array — used for horizontal centering on the bar column. */
  categoryIndex: number;
  value: number;
};

const OUTLIER_INNER_RADIUS_PX = 3.5;
const OUTLIER_OUTER_RADIUS_PX = 5.5;
const OUTLIER_OUTER_STROKE = "#101010";
const OUTLIER_OUTER_DASH = "2 2";

function buildOutlierDots(data: BoxPlotStackDatum[]): OutlierDot[] {
  const dots: OutlierDot[] = [];
  const seen = new Set<string>();

  for (let categoryIndex = 0; categoryIndex < data.length; categoryIndex++) {
    const row = data[categoryIndex];
    const whiskerMin = row.min;
    const whiskerMax = row.max;
    const outside = row.values.filter((v) => v < whiskerMin || v > whiskerMax);

    for (const value of outside) {
      const dedupeKey = `${row.key}|||${value}`;
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      dots.push({
        id: dedupeKey,
        categoryIndex,
        value,
      });
    }
  }

  return dots;
}

const OutlierDotsLayer: React.FC<
  {
    dots: OutlierDot[];
    /** Number of x-axis band slots (= chart data.length). */
    categoryCount: number;
    /** Inner circle fill — matches whisker color for the active layer. */
    innerColor: string;
  } & Record<string, unknown>
> = ({ dots, categoryCount, innerColor, ...chartProps }) => {
  const yAxisMap = chartProps?.yAxisMap as Record<number, { scale?: (v: number) => number }> | undefined;
  const yAxis = yAxisMap?.[0] ?? (yAxisMap ? Object.values(yAxisMap)[0] : undefined);
  const yScale = yAxis?.scale;
  const offset = chartProps?.offset as
    | { left?: number; width?: number; top?: number; height?: number }
    | undefined;

  if (!yScale || dots.length === 0 || categoryCount <= 0) return null;

  const plotLeft = offset?.left ?? 0;
  const plotWidth = offset?.width ?? 0;
  if (plotWidth <= 0) return null;
  const bandWidth = plotWidth / categoryCount;

  return (
    <g className="cement-outlier-dots" pointerEvents="none" aria-hidden>
      {dots.map((dot) => {
        const cx = plotLeft + (dot.categoryIndex + 0.5) * bandWidth;
        const cy = yScale(dot.value);
        if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

        return (
          <g key={dot.id}>
            <circle
              cx={cx}
              cy={cy}
              r={OUTLIER_OUTER_RADIUS_PX}
              fill="none"
              stroke={OUTLIER_OUTER_STROKE}
              strokeWidth={1.5}
              strokeDasharray={OUTLIER_OUTER_DASH}
            />
            <circle
              cx={cx}
              cy={cy}
              r={OUTLIER_INNER_RADIUS_PX}
              fill={innerColor}
              stroke="none"
            />
          </g>
        );
      })}
    </g>
  );
};

const PartitionsLayer: React.FC<{
  bandPx: number;
  totalSlots: number;
  materials: string[];
  processesByMaterial: Map<string, string[]>;
  timelinesCount: number;
}> = ({ bandPx, totalSlots, materials, processesByMaterial, timelinesCount }) => {
  // User requested: remove internal graph partition lines.
  void bandPx;
  void totalSlots;
  void materials;
  void processesByMaterial;
  void timelinesCount;
  return null;
};

const AxisSectionsLayer: React.FC<
  {
    dataKeys: string[];
    timelineLabels: string[];
    technologyAxisSpans: { start: number; span: number; label: string }[];
    subMaterialAxisSpans: { start: number; span: number; label: string }[];
    timelinesCount: number;
    baseMaterialLabel: string;
    showTimelineFooter: boolean;
    showTechnologyFooter: boolean;
    showSubMaterialFooter: boolean;
    showBaseMaterialFooter: boolean;
  } & any
> = ({
  dataKeys,
  timelineLabels,
  technologyAxisSpans,
  subMaterialAxisSpans,
  timelinesCount,
  baseMaterialLabel,
  showTimelineFooter,
  showTechnologyFooter,
  showSubMaterialFooter,
  showBaseMaterialFooter,
  ...chartProps
}) => {
  const offset = chartProps?.offset;
  if (!offset || !dataKeys.length || timelinesCount <= 0) return null;

  const x0 = offset.left ?? 0;
  const y0 = (offset.top ?? 0) + (offset.height ?? 0);
  const plotW = offset.width ?? 0;
  if (plotW <= 0) return null;

  const totalSlots = dataKeys.length;
  const bandPx = plotW / totalSlots;

  const rowH = AXIS_SECTION_ROW_H;
  const gap = 4;

  let nextRowY = y0 + 6;
  const allocateRow = () => {
    const y = nextRowY;
    nextRowY += rowH + gap;
    return y;
  };

  const timelineY = showTimelineFooter ? allocateRow() : null;
  const technologyRowY = showTechnologyFooter ? allocateRow() : null;
  const subMaterialRowY = showSubMaterialFooter ? allocateRow() : null;
  const materialParentY = showBaseMaterialFooter ? allocateRow() : null;

  const stroke = "#D9D9D9";
  const fill = "#ffffff";

  const renderSpanRow = (
    spans: { start: number; span: number; label: string }[],
    y: number,
    keyPrefix: string
  ) => (
    <g>
      {spans.map((s) => {
        const x = x0 + s.start * bandPx;
        const w = Math.max(0, s.span * bandPx);
        const raw = (s.label ?? "").trim();
        const displayLabel = raw === "" ? "—" : raw;
        return (
          <g key={`${keyPrefix}-${s.start}-${s.span}`}>
            <rect x={x} y={y} width={w} height={rowH} fill={fill} stroke={stroke} strokeWidth={1} />
            <foreignObject x={x} y={y} width={w} height={rowH} style={{ overflow: "hidden" }}>
              <div className="cement-axis-submat-wrap" title={displayLabel}>
                {displayLabel}
              </div>
            </foreignObject>
          </g>
        );
      })}
    </g>
  );

  const renderRowHeader = (label: string, y: number) => {
    const width = Math.max(0, x0 - 8);
    if (width <= 0) return null;

    return (
      <foreignObject x={0} y={y} width={width} height={rowH} style={{ overflow: "hidden" }}>
        <div className="cement-axis-row-header" title={label}>
          {label}
        </div>
      </foreignObject>
    );
  };

  return (
    <g className="cement-axis-sections" pointerEvents="none">
      {timelineY !== null && renderRowHeader("Time representativeness", timelineY)}
      {technologyRowY !== null && renderRowHeader("Manufacturing technology", technologyRowY)}
      {subMaterialRowY !== null && renderRowHeader("Material sub-type", subMaterialRowY)}
      {materialParentY !== null && renderRowHeader("Material", materialParentY)}

      {/* Row 1: Timeline (date) */}
      {timelineY !== null && (
        <g>
        {timelineLabels.map((label: string, i: number) => {
          const x = x0 + i * bandPx;
          const w = bandPx;
          const displayLabel = (label ?? "").trim() || "—";
          return (
            <g key={`axis-timeline-${i}`}>
              <rect x={x} y={timelineY} width={w} height={rowH} fill={fill} stroke={stroke} strokeWidth={1} />
              <foreignObject x={x} y={timelineY} width={w} height={rowH} style={{ overflow: "hidden" }}>
                <div className="cement-axis-submat-wrap" title={displayLabel}>
                  {displayLabel}
                </div>
              </foreignObject>
            </g>
          );
        })}
        </g>
      )}

      {/* Manufacturing Technology */}
      {technologyRowY !== null && renderSpanRow(technologyAxisSpans, technologyRowY, "axis-tech")}

      {/* Material Sub-type */}
      {subMaterialRowY !== null && renderSpanRow(subMaterialAxisSpans, subMaterialRowY, "axis-submat")}

      {/* Material (parent) */}
      {materialParentY !== null && (
        <g>
          <rect
            x={x0}
            y={materialParentY}
            width={plotW}
            height={rowH}
            fill={fill}
            stroke={stroke}
            strokeWidth={1}
          />
          <text
            x={x0 + plotW / 2}
            y={materialParentY + rowH / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#101010"
            fontSize={12}
            fontWeight={600}
          >
            {(baseMaterialLabel ?? "").trim() || "—"}
          </text>
        </g>
      )}
    </g>
  );
};

const HorizonBar = (props: RectangleProps) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) return null;

  return (
    <line
      x1={x}
      y1={y}
      x2={x + width}
      y2={y}
      stroke={BOX_STROKE}
      strokeWidth={2}
      strokeLinecap="round"
    />
  );
};

const DotBar = (props: RectangleProps) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) return null;

  return (
    <line
      x1={x + width / 2}
      y1={y + height}
      x2={x + width / 2}
      y2={y}
      stroke={BOX_STROKE}
      strokeWidth={3}
      strokeDasharray="5 4"
      strokeLinecap="round"
    />
  );
};

const HorizonBarHighlight = (props: RectangleProps) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) return null;

  return (
    <line
      x1={x}
      y1={y}
      x2={x + width}
      y2={y}
      stroke={BOX_STROKE_HIGHLIGHT}
      strokeWidth={2}
      strokeLinecap="round"
    />
  );
};

const DotBarHighlight = (props: RectangleProps) => {
  const { x, y, width, height } = props;
  if (x == null || y == null || width == null || height == null) return null;

  return (
    <line
      x1={x + width / 2}
      y1={y + height}
      x2={x + width / 2}
      y2={y}
      stroke={BOX_STROKE_HIGHLIGHT}
      strokeWidth={3}
      strokeDasharray="5 4"
      strokeLinecap="round"
    />
  );
};

function inferBaseMaterialName(dataset: CementBoxPlotDataset): string | null {
  const bg = dataset.background;
  if (!bg) return null;
  const name =
    bg.material?.name ??
    bg.materialName ??
    bg.material_name ??
    bg.materialCode ??
    bg.material_code ??
    bg.material?.code ??
    null;
  return name ? String(name).trim() || null : null;
}

function inferMaterialSubType(dataset: CementBoxPlotDataset): string {
  const bg = dataset.background;
  const fromName =
    bg?.subMaterialIdName ??
    bg?.sub_material_id_name ??
    bg?.subMaterialName ??
    bg?.sub_material_name ??
    bg?.subMaterialUnit ??
    bg?.sub_material_unit ??
    null;
  if (fromName && String(fromName).trim()) return String(fromName).trim();

  const fromObj = bg?.subMaterial?.code ?? bg?.subMaterial?.name ?? null;
  if (fromObj && String(fromObj).trim()) return String(fromObj).trim();

  const title = (dataset.title ?? "").toUpperCase();
  if (title.includes("PPC")) return "PPC";
  if (title.includes("PSC")) return "PSC";
  if (title.includes("OPC")) return "OPC";

  return "Unknown";
}

/** Default label for the total column when sub-type split is enabled. */
const AGNOSTIC_SUB_MATERIAL_COLUMN = "Agnostic";

/** Canonical map key for any Agnostic-labelled sub-type. */
function normalizeSubMaterialNorm(raw: string): string {
  const norm = raw.trim().toLowerCase();
  if (!norm) return norm;
  const compact = norm.replace(/[^a-z0-9]/g, "");
  if (norm === "agnostic" || compact === "agnostic") return "agnostic";
  return norm;
}

/** Agnostic sub-type column shows pooled data from every sub-type (total). */
function isAgnosticSubMaterial(label: string): boolean {
  return normalizeSubMaterialNorm(label) === "agnostic";
}

const PREFERRED_SUB_MATERIALS = ["OPC", "PPC", "PSC"];

function compareSubMaterialLabels(a: string, b: string): number {
  const aAgnostic = isAgnosticSubMaterial(a);
  const bAgnostic = isAgnosticSubMaterial(b);
  if (aAgnostic && !bAgnostic) return -1;
  if (!aAgnostic && bAgnostic) return 1;
  const ia = PREFERRED_SUB_MATERIALS.indexOf(a);
  const ib = PREFERRED_SUB_MATERIALS.indexOf(b);
  if (ia !== -1 || ib !== -1) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  return a.localeCompare(b);
}

/** Ensures Agnostic exists in the map and returns its display label. */
function ensureAgnosticSubMaterialColumn(
  subMaterialCanonical: Map<string, string>
): string {
  const norm = "agnostic";
  if (!subMaterialCanonical.has(norm)) {
    subMaterialCanonical.set(norm, AGNOSTIC_SUB_MATERIAL_COLUMN);
  }
  return subMaterialCanonical.get(norm)!;
}

function orderedSubMaterialLabels(subMaterialCanonical: Map<string, string>): string[] {
  return Array.from(subMaterialCanonical.values()).sort(compareSubMaterialLabels);
}

/** Union of all manufacturing technologies across every sub-type (for Agnostic + technology split). */
function allTechnologyLabels(
  techsBySubMat: Map<string, Set<string>>
): string[] {
  const all = new Set<string>();
  techsBySubMat.forEach((set) => {
    set.forEach((t) => all.add(t));
  });
  const techs = Array.from(all).sort((a, b) => a.localeCompare(b));
  return techs.length > 0 ? techs : ["All"];
}

function processesForSubMaterialLayout(
  material: string,
  techsBySubMat: Map<string, Set<string>>
): string[] {
  if (isAgnosticSubMaterial(material)) {
    return allTechnologyLabels(techsBySubMat);
  }
  const techs = Array.from(techsBySubMat.get(material) ?? new Set(["All"])).sort((a, b) =>
    a.localeCompare(b)
  );
  return techs.length > 0 ? techs : ["All"];
}

/** Per-dataset column: own sub-type only (Agnostic total column is filled separately). */
function chartMaterialKeysForDataset(
  d: CementBoxPlotDataset,
  opts: {
    splitBySubMaterial: boolean;
    splitByTechnology: boolean;
    baseMaterialLabel: string;
    subMaterialKey: (dataset: CementBoxPlotDataset) => string;
    technologyKey: (dataset: CementBoxPlotDataset) => string;
  }
): string[] {
  const { splitBySubMaterial, splitByTechnology, baseMaterialLabel, subMaterialKey, technologyKey } =
    opts;

  if (!splitBySubMaterial) {
    return [splitByTechnology ? technologyKey(d) : baseMaterialLabel];
  }

  return [subMaterialKey(d)];
}

/**
 * Agnostic column = all datasets in the same process + timeline (every sub-type, including Agnostic).
 */
function refillAgnosticSlotsFromAllSubTypes(params: {
  splitBySubMaterial: boolean;
  splitByTechnology: boolean;
  agnosticSubMaterialLabel: string;
  slotKeys: { material: string; process: string; timeline: string }[];
  filteredDatasets: CementBoxPlotDataset[];
  timeSlicer: TimeSlicer;
  technologyKey: (dataset: CementBoxPlotDataset) => string;
  carbonMap: Map<string, number[]>;
  energyMap: Map<string, number[]>;
  slotToDatasetIdsCarbon: Map<string, string[]>;
  slotToDatasetIdsEnergy: Map<string, string[]>;
  slotToSubmissionCodesCarbon: Map<string, string[]>;
  slotToSubmissionCodesEnergy: Map<string, string[]>;
  pushId: (map: Map<string, string[]>, key: string, id: string) => void;
  pushSubmissionCode: (map: Map<string, string[]>, key: string, code: string) => void;
  includeIds?: boolean;
}): void {
  const {
    splitBySubMaterial,
    splitByTechnology,
    agnosticSubMaterialLabel,
    slotKeys,
    filteredDatasets,
    timeSlicer,
    technologyKey,
    carbonMap,
    energyMap,
    slotToDatasetIdsCarbon,
    slotToDatasetIdsEnergy,
    slotToSubmissionCodesCarbon,
    slotToSubmissionCodesEnergy,
    pushId,
    pushSubmissionCode,
    includeIds = true,
  } = params;

  if (!splitBySubMaterial) return;

  for (const k of slotKeys) {
    if (!isAgnosticSubMaterial(k.material)) continue;

    const slotKey = makeSlotKey(agnosticSubMaterialLabel, k.process, k.timeline);
    carbonMap.set(slotKey, []);
    energyMap.set(slotKey, []);
    if (includeIds) {
      slotToDatasetIdsCarbon.set(slotKey, []);
      slotToDatasetIdsEnergy.set(slotKey, []);
      slotToSubmissionCodesCarbon.set(slotKey, []);
      slotToSubmissionCodesEnergy.set(slotKey, []);
    }

    for (const d of filteredDatasets) {
      const process =
        splitBySubMaterial && splitByTechnology ? technologyKey(d) : "All";
      const timeline = bucketForTime(datasetTimeMs(d), timeSlicer).label;
      if (process !== k.process || timeline !== k.timeline) continue;

      const summary = d.a1a3Summary;
      const carbonValue = toNumber(summary?.embodiedCarbonValue);
      const energyValue = toNumber(summary?.embodiedEnergyValue);
      const sid = String(d.id ?? "");
      const subCode =
        String(d.submissionCode ?? d.submission_code ?? "").trim() || sid || "";

      if (carbonValue !== null) {
        const arr = carbonMap.get(slotKey) ?? [];
        arr.push(carbonValue);
        carbonMap.set(slotKey, arr);
        if (includeIds) {
          if (sid) pushId(slotToDatasetIdsCarbon, slotKey, sid);
          if (subCode) pushSubmissionCode(slotToSubmissionCodesCarbon, slotKey, subCode);
        }
      }

      if (energyValue !== null) {
        const arr = energyMap.get(slotKey) ?? [];
        arr.push(energyValue);
        energyMap.set(slotKey, arr);
        if (includeIds) {
          if (sid) pushId(slotToDatasetIdsEnergy, slotKey, sid);
          if (subCode) pushSubmissionCode(slotToSubmissionCodesEnergy, slotKey, subCode);
        }
      }
    }
  }
}

function inferManufacturingTechnology(dataset: CementBoxPlotDataset): string {
  const bg = dataset.background;
  const name =
    bg?.technologyIdName ??
    bg?.technology?.name ??
    bg?.manufacturingProcess ??
    null;
  return name && String(name).trim() ? String(name).trim() : "Unknown";
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number.parseFloat(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Type definition — Cement Box Plot Metric. */
export type CementBoxPlotMetric = "carbon" | "energy";

type Metric = CementBoxPlotMetric;

/** Type definition — Cement Box Plot Slot Selection. */
export interface CementBoxPlotSlotSelection {
  metric: CementBoxPlotMetric;
  slotKey: string;
  material: string;
  process: string;
  timeline: string;
  datasetIds: readonly string[];
  /** Submission codes for datasets in this column (same order as grouping; falls back to id if missing). */
  submissionCodes: readonly string[];
}


function computeYDomainFromSlots(slots: ComputedSlot[]) {
  const maxValue = Math.max(
    0,
    ...slots.flatMap((s) => {
      const vals: number[] = [];
      if (s.stats.hasData) {
        if (Number.isFinite(s.stats.dataMax)) vals.push(s.stats.dataMax);
        else if (Number.isFinite(s.stats.max)) vals.push(s.stats.max);
      }
      if (s.highlightStats.hasData) {
        if (Number.isFinite(s.highlightStats.dataMax)) vals.push(s.highlightStats.dataMax);
        else if (Number.isFinite(s.highlightStats.max)) vals.push(s.highlightStats.max);
      }
      return vals;
    })
  );

  /** Keep 10% headroom above the highest value in the plot (including outliers). */
  const Y_AXIS_HEADROOM = 1.1;
  const yMax =
    maxValue <= 0
      ? 1
      : parseFloat((maxValue * Y_AXIS_HEADROOM).toFixed(10));

  // 10 equal sub-divisions so sub-units like 0.1, 0.2 are visible
  const step = parseFloat((yMax / 10).toFixed(10));
  const ticks = Array.from({ length: 11 }, (_, i) => parseFloat((i * step).toFixed(10)));
  return { yMax, ticks };
}

function metricAxisTitle(metric: Metric) {
  return metric === "energy" ? "Cradle to Gate Embodied Energy" : "Cradle to Gate Embodied Carbon";
}

function metricAxisUnit(metric: Metric) {
  return metric === "energy" ? "(MJ/kg)" : "(kg CO\u2082eq/kg)";
}

function metricAria(metric: Metric) {
  return metric === "energy" ? "embodied energy" : "embodied carbon";
}

function slotsToBoxPlotStackData(
  slots: ComputedSlot[],
  useHighlight: boolean
): BoxPlotStackDatum[] {
  return slots.map((slot, idx) => {
    const s = useHighlight ? slot.highlightStats : slot.stats;
    const values = useHighlight ? slot.highlightValues : slot.values;
    const min = s.min;
    const q1 = s.q1;
    const median = s.median;
    const q3 = s.q3;
    const max = s.max;

    return {
      key: `${slot.material}|||${slot.process}|||${slot.timeline}|||${idx}`,
      material: slot.material,
      process: slot.process,
      timeline: slot.timeline,
      min,
      bottomWhisker: Math.max(0, q1 - min),
      bottomBox: Math.max(0, median - q1),
      topBox: Math.max(0, q3 - median),
      topWhisker: Math.max(0, max - q3),
      bar: 0,
      q1,
      median,
      q3,
      max,
      dataMin: s.dataMin,
      dataMax: s.dataMax,
      outliers: s.outliers,
      values,
      count: s.count,
    };
  });
}

/** ~px length if the full label were rotated along the plot height (used to pick 1 vs 2 lines). */
function approxRotatedLabelExtentPx(metric: Metric): number {
  const full = `${metricAxisTitle(metric)} ${metricAxisUnit(metric)}`;
  return full.length * 6.6;
}

/* ══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════════════ */

const CementBoxPlotChart: React.FC<Props> = ({
  datasets,
  showFilters = false,
  parentMaterialName,
  highlightDatasetIds,
  showHighlightLegend = false,
  onSlotClick,
  interactiveSlotSelection = false,
  onInteractiveMetricChange,
  metric: metricProp,
  onMetricChange,
}) => {
  const highlightIdsKey =
    highlightDatasetIds == null
      ? "__hl_off__"
      : [...highlightDatasetIds].map(String).sort().join("\u0001");
  const [filters, setFilters] = useState<BoxPlotFilters>({
    materialSubType: "Yes",
    manufacturingTechnology: "No",
    timeSlicer: "10 Years",
  });
  const [internalMetric, setInternalMetric] = useState<Metric>("carbon");
  const metric = metricProp ?? internalMetric;
  const chartInnerRef = useRef<HTMLDivElement | null>(null);
  const prevMetricRef = useRef<Metric>(metric);

  const handleMetricChange = (next: Metric) => {
    if (metricProp === undefined) {
      setInternalMetric(next);
    }
    onMetricChange?.(next);
  };

  useEffect(() => {
    if (!interactiveSlotSelection || !onInteractiveMetricChange) {
      prevMetricRef.current = metric;
      return;
    }
    if (prevMetricRef.current !== metric) {
      onInteractiveMetricChange();
    }
    prevMetricRef.current = metric;
  }, [metric, interactiveSlotSelection, onInteractiveMetricChange]);

  const filteredDatasets = useMemo<CementBoxPlotDataset[]>(() => {
    return datasets ?? [];
  }, [datasets]);

  const charts = useMemo(() => {
    const hasInput = filteredDatasets.length > 0;
    const trackHighlight = highlightDatasetIds != null;
    const highlightIdSet = new Set((highlightDatasetIds ?? []).map(String));

    const activeTimelines = (() => {
      const map = new Map<string, number>();
      for (const d of filteredDatasets) {
        const { label, sortMs } = bucketForTime(datasetTimeMs(d), filters.timeSlicer);
        const prev = map.get(label);
        if (prev == null || sortMs < prev) map.set(label, sortMs);
      }

      if (map.size === 0) return [];

      let timelineMap = map;

      // "10 Years": fill every bucket between min/max years so the axis remains contiguous.
      if (filters.timeSlicer === "10 Years") {
        let minY = Infinity;
        let maxY = -Infinity;
        for (const d of filteredDatasets) {
          const t = datasetTimeMs(d);
          if (t == null || !Number.isFinite(t)) continue;
          const y = new Date(t).getFullYear();
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
        if (Number.isFinite(minY) && Number.isFinite(maxY)) {
          timelineMap = expandTenYearSlicerBuckets(map, minY, maxY);
        }
      }

      return Array.from(timelineMap.entries())
        .sort((a, b) => a[1] - b[1])
        .map(([label]) => label);
    })();

    const baseNames = filteredDatasets
      .map((d) => inferBaseMaterialName(d))
      .filter((v): v is string => Boolean(v));
    const inferredBaseLabel =
      baseNames.length > 0
        ? Array.from(new Set(baseNames)).length === 1
          ? baseNames[0]
          : "Materials"
        : null;
    const baseMaterialLabel =
      (parentMaterialName && String(parentMaterialName).trim()) ||
      inferredBaseLabel ||
      "Material";

    // Columns: sub-material × date category (each sub-material block repeats all timeline buckets).
    const subMaterialCanonical = new Map<string, string>();
    for (const d of filteredDatasets) {
      const raw = inferMaterialSubType(d).trim();
      if (!raw) continue;
      const norm = normalizeSubMaterialNorm(raw);
      if (!subMaterialCanonical.has(norm)) {
        subMaterialCanonical.set(
          norm,
          norm === "agnostic" ? AGNOSTIC_SUB_MATERIAL_COLUMN : raw
        );
      }
    }

    const subMaterialKey = (d: CementBoxPlotDataset) => {
      const raw = inferMaterialSubType(d).trim();
      const norm = normalizeSubMaterialNorm(raw);
      return subMaterialCanonical.get(norm) ?? raw;
    };

    // Technology canonical map (similar to subMaterialCanonical)
    const technologyCanonical = new Map<string, string>();
    for (const d of filteredDatasets) {
      const raw = inferManufacturingTechnology(d).trim();
      if (!raw) continue;
      const norm = raw.toLowerCase();
      if (!technologyCanonical.has(norm)) technologyCanonical.set(norm, raw);
    }
    const technologyKey = (d: CementBoxPlotDataset) => {
      const raw = inferManufacturingTechnology(d).trim();
      return technologyCanonical.get(raw.toLowerCase()) ?? raw;
    };

    const splitBySubMaterial = filters.materialSubType === "Yes";
    const splitByTechnology = filters.manufacturingTechnology === "Yes";
    const agnosticSubMaterialLabel = splitBySubMaterial
      ? ensureAgnosticSubMaterialColumn(subMaterialCanonical)
      : null;

    /** Slot count for a given Sub-type / Technology split (used to fix minimum chart width). */
    const countSlotsForSplits = (splitSub: boolean, splitTech: boolean): number => {
      let mats: string[] = [];
      const procMap = new Map<string, string[]>();

      if (!splitSub && !splitTech) {
        mats = [baseMaterialLabel];
        procMap.set(baseMaterialLabel, ["All"]);
      } else if (splitSub && !splitTech) {
        ensureAgnosticSubMaterialColumn(subMaterialCanonical);
        mats = orderedSubMaterialLabels(subMaterialCanonical);
        for (const m of mats) procMap.set(m, ["All"]);
      } else if (!splitSub && splitTech) {
        const techs = Array.from(technologyCanonical.values()).sort((a, b) => a.localeCompare(b));
        mats = techs.length > 0 ? techs : [baseMaterialLabel];
        for (const m of mats) procMap.set(m, ["All"]);
      } else {
        ensureAgnosticSubMaterialColumn(subMaterialCanonical);
        mats = orderedSubMaterialLabels(subMaterialCanonical);
        const techsBySubMat = new Map<string, Set<string>>();
        for (const d of filteredDatasets) {
          const sm = subMaterialKey(d);
          const tk = technologyKey(d);
          if (!techsBySubMat.has(sm)) techsBySubMat.set(sm, new Set());
          techsBySubMat.get(sm)!.add(tk);
        }
        for (const m of mats) {
          procMap.set(m, processesForSubMaterialLayout(m, techsBySubMat));
        }
      }

      if (mats.length === 0 && activeTimelines.length > 0) {
        mats = [baseMaterialLabel];
        procMap.set(baseMaterialLabel, ["All"]);
      }

      let n = 0;
      for (const m of mats) {
        const procs = procMap.get(m) ?? ["All"];
        n += procs.length * activeTimelines.length;
      }
      return n;
    };

    // Widest layout among all filter combos so toggling Sub-type or Technology never shrinks the plot width.
    const minWidthSlotCount = Math.max(
      countSlotsForSplits(false, false),
      countSlotsForSplits(true, false),
      countSlotsForSplits(false, true),
      countSlotsForSplits(true, true)
    );

    let layoutMaterials: string[] = [];
    const processesByMaterial = new Map<string, string[]>();

    if (!splitBySubMaterial && !splitByTechnology) {
      layoutMaterials = [baseMaterialLabel];
      processesByMaterial.set(baseMaterialLabel, ["All"]);
    } else if (splitBySubMaterial && !splitByTechnology) {
      layoutMaterials = orderedSubMaterialLabels(subMaterialCanonical);
      for (const m of layoutMaterials) processesByMaterial.set(m, ["All"]);
    } else if (!splitBySubMaterial && splitByTechnology) {
      const techs = Array.from(technologyCanonical.values()).sort((a, b) => a.localeCompare(b));
      layoutMaterials = techs.length > 0 ? techs : [baseMaterialLabel];
      for (const m of layoutMaterials) processesByMaterial.set(m, ["All"]);
    } else {
      // Both Yes: subMaterials as outer groups, technologies as inner processes
      layoutMaterials = orderedSubMaterialLabels(subMaterialCanonical);
      const techsBySubMat = new Map<string, Set<string>>();
      for (const d of filteredDatasets) {
        const sm = subMaterialKey(d);
        const tk = technologyKey(d);
        if (!techsBySubMat.has(sm)) techsBySubMat.set(sm, new Set());
        techsBySubMat.get(sm)!.add(tk);
      }
      for (const m of layoutMaterials) {
        processesByMaterial.set(m, processesForSubMaterialLayout(m, techsBySubMat));
      }
    }

    if (layoutMaterials.length === 0 && activeTimelines.length > 0) {
      layoutMaterials = [baseMaterialLabel];
      processesByMaterial.set(baseMaterialLabel, ["All"]);
    }

    const keysInOrder: { material: string; process: string; timeline: string }[] = [];
    for (const m of layoutMaterials) {
      const procs = processesByMaterial.get(m) ?? ["All"];
      for (const p of procs) {
        for (const t of activeTimelines) {
          keysInOrder.push({ material: m, process: p, timeline: t });
        }
      }
    }

    const totalSlotsForLayout = keysInOrder.length;
    const slotWidthForLayout = totalSlotsForLayout > 0 ? PLOT_WIDTH / totalSlotsForLayout : PLOT_WIDTH;

    // Pre-computed axis row spans — always 4 rows regardless of filter state
    const subMaterialAxisSpans: { start: number; span: number; label: string }[] = [];
    if (splitBySubMaterial) {
      let cursor = 0;
      for (const m of layoutMaterials) {
        const procs = processesByMaterial.get(m) ?? ["All"];
        const span = procs.length * activeTimelines.length;
        subMaterialAxisSpans.push({ start: cursor, span, label: m });
        cursor += span;
      }
    } else {
      subMaterialAxisSpans.push({ start: 0, span: totalSlotsForLayout, label: "All" });
    }

    const technologyAxisSpans: { start: number; span: number; label: string }[] = [];
    if (splitByTechnology) {
      if (splitBySubMaterial) {
        // technologies are processes nested within each sub-material
        let cursor = 0;
        for (const m of layoutMaterials) {
          const procs = processesByMaterial.get(m) ?? ["All"];
          for (const p of procs) {
            technologyAxisSpans.push({ start: cursor, span: activeTimelines.length, label: p });
            cursor += activeTimelines.length;
          }
        }
      } else {
        // technologies are layoutMaterials
        let cursor = 0;
        for (const m of layoutMaterials) {
          technologyAxisSpans.push({ start: cursor, span: activeTimelines.length, label: m });
          cursor += activeTimelines.length;
        }
      }
    } else {
      technologyAxisSpans.push({ start: 0, span: totalSlotsForLayout, label: "All" });
    }

    const carbonMap = new Map<string, number[]>();
    const energyMap = new Map<string, number[]>();
    const slotToDatasetIdsCarbon = new Map<string, string[]>();
    const slotToDatasetIdsEnergy = new Map<string, string[]>();
    const slotToSubmissionCodesCarbon = new Map<string, string[]>();
    const slotToSubmissionCodesEnergy = new Map<string, string[]>();

    let usableCarbon = 0;
    let usableEnergy = 0;

    const pushId = (map: Map<string, string[]>, key: string, id: string) => {
      const arr = map.get(key) ?? [];
      if (!arr.includes(id)) arr.push(id);
      map.set(key, arr);
    };

    const pushSubmissionCode = (map: Map<string, string[]>, key: string, code: string) => {
      if (!code) return;
      const arr = map.get(key) ?? [];
      if (!arr.includes(code)) arr.push(code);
      map.set(key, arr);
    };

    const chartMaterialSlotOpts = {
      splitBySubMaterial,
      splitByTechnology,
      baseMaterialLabel,
      subMaterialKey,
      technologyKey,
    };

    for (const d of filteredDatasets) {
      const summary = d.a1a3Summary;
      const carbonValue = toNumber(summary?.embodiedCarbonValue);
      const energyValue = toNumber(summary?.embodiedEnergyValue);
      const process = splitBySubMaterial && splitByTechnology ? technologyKey(d) : "All";
      const timeline = bucketForTime(datasetTimeMs(d), filters.timeSlicer).label;
      const sid = String(d.id ?? "");
      const subCode =
        String(d.submissionCode ?? d.submission_code ?? "").trim() || sid || "";

      let countedCarbon = false;
      let countedEnergy = false;
      const materialColumns = chartMaterialKeysForDataset(d, chartMaterialSlotOpts);

      for (const material of materialColumns) {
        const slotKey = makeSlotKey(material, process, timeline);

        if (carbonValue !== null) {
          if (!countedCarbon) {
            usableCarbon += 1;
            countedCarbon = true;
          }
          const arr = carbonMap.get(slotKey) ?? [];
          arr.push(carbonValue);
          carbonMap.set(slotKey, arr);
          if (sid) pushId(slotToDatasetIdsCarbon, slotKey, sid);
          if (subCode) pushSubmissionCode(slotToSubmissionCodesCarbon, slotKey, subCode);
        }

        if (energyValue !== null) {
          if (!countedEnergy) {
            usableEnergy += 1;
            countedEnergy = true;
          }
          const arr = energyMap.get(slotKey) ?? [];
          arr.push(energyValue);
          energyMap.set(slotKey, arr);
          if (sid) pushId(slotToDatasetIdsEnergy, slotKey, sid);
          if (subCode) pushSubmissionCode(slotToSubmissionCodesEnergy, slotKey, subCode);
        }
      }
    }

    if (splitBySubMaterial && agnosticSubMaterialLabel) {
      refillAgnosticSlotsFromAllSubTypes({
        splitBySubMaterial,
        splitByTechnology,
        agnosticSubMaterialLabel,
        slotKeys: keysInOrder,
        filteredDatasets,
        timeSlicer: filters.timeSlicer,
        technologyKey,
        carbonMap,
        energyMap,
        slotToDatasetIdsCarbon,
        slotToDatasetIdsEnergy,
        slotToSubmissionCodesCarbon,
        slotToSubmissionCodesEnergy,
        pushId,
        pushSubmissionCode,
      });
    }

    const highlightCarbonMap = new Map<string, number[]>();
    const highlightEnergyMap = new Map<string, number[]>();

    if (trackHighlight) {
      for (const d of filteredDatasets) {
        if (!highlightIdSet.has(String(d.id))) continue;
        const summary = d.a1a3Summary;
        const carbonValue = toNumber(summary?.embodiedCarbonValue);
        const energyValue = toNumber(summary?.embodiedEnergyValue);
        const process = splitBySubMaterial && splitByTechnology ? technologyKey(d) : "All";
        const timeline = bucketForTime(datasetTimeMs(d), filters.timeSlicer).label;

        for (const material of chartMaterialKeysForDataset(d, chartMaterialSlotOpts)) {
          const slotKey = makeSlotKey(material, process, timeline);

          if (carbonValue !== null) {
            const arr = highlightCarbonMap.get(slotKey) ?? [];
            arr.push(carbonValue);
            highlightCarbonMap.set(slotKey, arr);
          }
          if (energyValue !== null) {
            const arr = highlightEnergyMap.get(slotKey) ?? [];
            arr.push(energyValue);
            highlightEnergyMap.set(slotKey, arr);
          }
        }
      }

      if (splitBySubMaterial && agnosticSubMaterialLabel) {
        const highlightedOnly = filteredDatasets.filter((d) =>
          highlightIdSet.has(String(d.id))
        );
        refillAgnosticSlotsFromAllSubTypes({
          splitBySubMaterial,
          splitByTechnology,
          agnosticSubMaterialLabel,
          slotKeys: keysInOrder,
          filteredDatasets: highlightedOnly,
          timeSlicer: filters.timeSlicer,
          technologyKey,
          carbonMap: highlightCarbonMap,
          energyMap: highlightEnergyMap,
          slotToDatasetIdsCarbon,
          slotToDatasetIdsEnergy,
          slotToSubmissionCodesCarbon,
          slotToSubmissionCodesEnergy,
          pushId,
          pushSubmissionCode,
          includeIds: false,
        });
      }
    }

    const carbonSlots: ComputedSlot[] = [];
    const energySlots: ComputedSlot[] = [];
    for (let i = 0; i < keysInOrder.length; i++) {
      const k = keysInOrder[i];
      const xCenter = PLOT_LEFT + i * slotWidthForLayout + slotWidthForLayout / 2;

      const key = makeSlotKey(k.material, k.process, k.timeline);
      const cValues = (carbonMap.get(key) ?? []) as number[];
      const eValues = (energyMap.get(key) ?? []) as number[];
      const hcValues = (highlightCarbonMap.get(key) ?? []) as number[];
      const heValues = (highlightEnergyMap.get(key) ?? []) as number[];

      carbonSlots.push({
        material: k.material,
        process: k.process,
        timeline: k.timeline,
        stats: computeBoxStats(cValues),
        highlightStats: computeBoxStats(hcValues),
        values: cValues,
        highlightValues: hcValues,
        xCenter,
      });

      energySlots.push({
        material: k.material,
        process: k.process,
        timeline: k.timeline,
        stats: computeBoxStats(eValues),
        highlightStats: computeBoxStats(heValues),
        values: eValues,
        highlightValues: heValues,
        xCenter,
      });
    }

    const carbonDomain = computeYDomainFromSlots(carbonSlots);
    const energyDomain = computeYDomainFromSlots(energySlots);

    return {
      baseMaterialLabel,
      materials: layoutMaterials,
      processesByMaterial,
      timelines: activeTimelines,
      slotWidth: slotWidthForLayout,
      minWidthSlotCount,
      hasInput,
      trackHighlight,
      subMaterialAxisSpans,
      technologyAxisSpans,
      usable: { carbon: usableCarbon, energy: usableEnergy },
      carbon: { slots: carbonSlots, domain: carbonDomain },
      energy: { slots: energySlots, domain: energyDomain },
      slotToDatasetIdsByMetric: {
        carbon: slotToDatasetIdsCarbon,
        energy: slotToDatasetIdsEnergy,
      } as const,
      slotToSubmissionCodesByMetric: {
        carbon: slotToSubmissionCodesCarbon,
        energy: slotToSubmissionCodesEnergy,
      } as const,
    };
  }, [
    filteredDatasets,
    filters.materialSubType,
    filters.manufacturingTechnology,
    filters.timeSlicer,
    parentMaterialName,
    highlightDatasetIds,
    highlightIdsKey,
  ]);

  const formatTick = (tick: number) => tick.toFixed(2);

  const renderChart = (metric: Metric) => {
    const { slots, domain } = charts[metric];
    const { timelines, baseMaterialLabel } = charts;

    const hasAnyData = slots.some((s) => s.stats.hasData);
    const hasInput = charts.hasInput;
    const usable = charts.usable[metric];

    if (!hasInput) {
      return null;
    }

    if (!hasAnyData) {
      // Keep the UI clean: don't render empty messages.
      void usable;
      return null;
    }

    const data = slotsToBoxPlotStackData(slots, false);
    const hlData = slotsToBoxPlotStackData(slots, true);
    const outlierDots = buildOutlierDots(data);
    const hlOutlierDots = buildOutlierDots(hlData);
    const showHlOverlay = charts.trackHighlight && hlData.some((d) => d.count > 0);
    const showTimelineFooter = filters.timeSlicer !== "None";
    const showTechnologyFooter = filters.manufacturingTechnology === "Yes";
    const showSubMaterialFooter = filters.materialSubType === "Yes";
    const showBaseMaterialFooter = hasInput;
    const chartH = chartTotalHeightPx();
    const totalSlots = slots.length;
    const widthSlots = Math.max(totalSlots, charts.minWidthSlotCount || totalSlots);
    const minWidth = Math.max(
      860,
      widthSlots * SLOT_PX + CHART_MARGIN.left + CHART_MARGIN.right
    );

    const slotClickEnabled = interactiveSlotSelection && typeof onSlotClick === "function";
    const slotClickProps = slotClickEnabled
      ? {
          onClick: (entry: any) => {
            const payload = (entry?.payload ?? entry) as BoxPlotStackDatum | undefined;
            if (!payload || typeof payload.material !== "string") return;
            const slotKey = makeSlotKey(payload.material, payload.process, payload.timeline);
            const idMap = charts.slotToDatasetIdsByMetric[metric];
            const codeMap = charts.slotToSubmissionCodesByMetric[metric];
            const datasetIds = idMap.get(slotKey) ?? [];
            const submissionCodes = codeMap.get(slotKey) ?? [];
            onSlotClick!({
              metric,
              slotKey,
              material: payload.material,
              process: payload.process,
              timeline: payload.timeline,
              datasetIds,
              submissionCodes,
            });
          },
        }
      : {};

    const tooltip = ({ active, payload }: any) => {
      if (!active || !payload || payload.length === 0) return null;
      const p = payload[0]?.payload as BoxPlotStackDatum | undefined;
      if (!p) return null;
      const slotKeyHover = makeSlotKey(p.material, p.process, p.timeline);
      const hoverSubmissionCodes =
        charts.slotToSubmissionCodesByMetric[metric].get(slotKeyHover) ?? [];
      return (
        <div className="cement-tooltip">
          <div className="cement-tooltip-title">
            {p.material} • {p.timeline}
          </div>
          {hoverSubmissionCodes.length > 0 ? (
            <div className="cement-tooltip-codes">
              <div className="cement-tooltip-codes-label">Submission codes</div>
              <div className="cement-tooltip-codes-list">{hoverSubmissionCodes.join(", ")}</div>
            </div>
          ) : null}
          <div className="cement-tooltip-row">
            <span>n</span>
            <span>{p.count}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>lower whisker</span>
            <span>{formatTick(p.min)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>Q1</span>
            <span>{formatTick(p.q1)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>median</span>
            <span>{formatTick(p.median)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>Q3</span>
            <span>{formatTick(p.q3)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>upper whisker</span>
            <span>{formatTick(p.max)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>minimum</span>
            <span>{formatTick(p.dataMin)}</span>
          </div>
          <div className="cement-tooltip-row">
            <span>maximum</span>
            <span>{formatTick(p.dataMax)}</span>
          </div>
        </div>
      );
    };

    const xAxisHeight = FIXED_X_AXIS_FOOTER_HEIGHT_PX;
    const yAxisLine1 = metricAxisTitle(metric);
    const yAxisLine2 = metricAxisUnit(metric);

    return (
      <div className="cement-chart-scroll">
        <div
          className={`cement-chart-inner${slotClickEnabled ? " cement-chart-inner--interactive-slots" : ""}`}
          ref={chartInnerRef}
          style={{ minWidth, position: "relative" }}
        >
          <ResponsiveContainer width="100%" height={chartH}>
            <ComposedChart
              key={`main-${metric}-${filters.timeSlicer}-${data.length}`}
              data={data}
              margin={CHART_MARGIN}
              barCategoryGap={0}
              barGap={0}
            >
              <CartesianGrid strokeDasharray="4 8" stroke="#d4d4d4" opacity={0.6} />

              <XAxis
                dataKey="key"
                tick={false}
                height={xAxisHeight}
                axisLine={{ stroke: "#101010" }}
                padding={{ left: 0, right: 0 }}
                tickLine={false}
              />

              <YAxis
                key={`metric-y-axis-${metric}`}
                domain={[0, domain.yMax]}
                ticks={domain.ticks}
                tickFormatter={formatTick}
                tick={{ fontSize: 12, fill: "#101010" }}
                axisLine={{ stroke: "#101010" }}
                tickLine={false}
                width={Y_AXIS_WIDTH_PX}
                tickMargin={6}
                label={{
                  content: ({ viewBox }: any) => {
                    const { x, y, height } = viewBox ?? {};
                    if (x == null || y == null || height == null) return null;
                    const needTwoLines = height + 32 < approxRotatedLabelExtentPx(metric);

                    if (needTwoLines) {
                      const foX = x - Y_AXIS_LABEL_COL_W + 10;
                      return (
                        <foreignObject
                          x={foX}
                          y={y}
                          width={Y_AXIS_LABEL_COL_W}
                          height={height}
                          style={{ overflow: "visible", pointerEvents: "none" }}
                        >
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "center",
                              textAlign: "center",
                              fontSize: 11,
                              fontWeight: 600,
                              lineHeight: 1.35,
                              color: "#101010",
                              fontFamily:
                                'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
                              padding: "0 4px",
                              boxSizing: "border-box",
                            }}
                          >
                            <div>{yAxisLine1}</div>
                            <div style={{ marginTop: 2 }}>{yAxisLine2}</div>
                          </div>
                        </foreignObject>
                      );
                    }

                    const cx = x - 8;
                    const cy = y + height / 2;
                    const yAxisLabel = `${yAxisLine1} ${yAxisLine2}`;
                    return (
                      <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#101010"
                        fontSize={12}
                        fontWeight={600}
                        transform={`rotate(-90, ${cx}, ${cy})`}
                      >
                        {yAxisLabel}
                      </text>
                    );
                  },
                }}
              />

              <Tooltip
                content={tooltip}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{ zIndex: 10050 }}
              />

              {/* Sections inside X-axis area (below ticks) */}
              <Customized
                component={
                  <AxisSectionsLayer
                    dataKeys={data.map((d) => d.key)}
                    timelineLabels={data.map((d) => d.timeline)}
                    technologyAxisSpans={charts.technologyAxisSpans}
                    subMaterialAxisSpans={charts.subMaterialAxisSpans}
                    timelinesCount={timelines.length}
                    baseMaterialLabel={baseMaterialLabel}
                    showTimelineFooter={showTimelineFooter}
                    showTechnologyFooter={showTechnologyFooter}
                    showSubMaterialFooter={showSubMaterialFooter}
                    showBaseMaterialFooter={showBaseMaterialFooter}
                  />
                }
              />

              {/* Boxplot as stacked bars */}
              <Bar stackId="a" dataKey="min" fill="transparent" barSize={BAR_SIZE_PX} {...slotClickProps} />
              {/* min cap */}
              <Bar stackId="a" dataKey="bar" shape={<HorizonBar />} barSize={BAR_SIZE_PX} {...slotClickProps} />
              <Bar stackId="a" dataKey="bottomWhisker" shape={<DotBar />} barSize={BAR_SIZE_PX} {...slotClickProps} />
              <Bar stackId="a" dataKey="bottomBox" fill={BOX_FILL} barSize={BAR_SIZE_PX} {...slotClickProps} />
              {/* median line */}
              <Bar stackId="a" dataKey="bar" shape={<HorizonBar />} barSize={BAR_SIZE_PX} {...slotClickProps} />
              <Bar stackId="a" dataKey="topBox" fill={BOX_FILL} barSize={BAR_SIZE_PX} {...slotClickProps} />
              <Bar stackId="a" dataKey="topWhisker" shape={<DotBar />} barSize={BAR_SIZE_PX} {...slotClickProps} />
              {/* max cap */}
              <Bar stackId="a" dataKey="bar" shape={<HorizonBar />} barSize={BAR_SIZE_PX} {...slotClickProps} />
            </ComposedChart>
          </ResponsiveContainer>

          {showHlOverlay ? (
            <div
              className="cement-chart-overlay"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                pointerEvents: "none",
              }}
              aria-hidden
            >
              <ResponsiveContainer width="100%" height={chartH}>
                <ComposedChart
                  data={hlData}
                  margin={CHART_MARGIN}
                  barCategoryGap={0}
                  barGap={0}
                >
                  <CartesianGrid strokeDasharray="4 8" stroke="transparent" />
                  <XAxis
                    dataKey="key"
                    tick={false}
                    height={xAxisHeight}
                    axisLine={false}
                    padding={{ left: 0, right: 0 }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, domain.yMax]}
                    ticks={domain.ticks}
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                    width={Y_AXIS_WIDTH_PX}
                    tickMargin={6}
                  />
                  <Bar stackId="a" dataKey="min" fill="transparent" barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="bar" shape={<HorizonBarHighlight />} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="bottomWhisker" shape={<DotBarHighlight />} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="bottomBox" fill={BOX_FILL_HIGHLIGHT} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="bar" shape={<HorizonBarHighlight />} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="topBox" fill={BOX_FILL_HIGHLIGHT} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="topWhisker" shape={<DotBarHighlight />} barSize={BAR_SIZE_PX} />
                  <Bar stackId="a" dataKey="bar" shape={<HorizonBarHighlight />} barSize={BAR_SIZE_PX} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : null}

          {outlierDots.length > 0 || hlOutlierDots.length > 0 ? (
            <div
              className="cement-chart-outlier-layer"
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                right: 0,
                bottom: 0,
                pointerEvents: "none",
              }}
              aria-hidden
            >
              <ResponsiveContainer width="100%" height={chartH}>
                <ComposedChart
                  key={`outliers-${metric}-${filters.timeSlicer}-${data.length}`}
                  data={data}
                  margin={CHART_MARGIN}
                  barCategoryGap={0}
                  barGap={0}
                >
                  <XAxis
                    dataKey="key"
                    tick={false}
                    height={xAxisHeight}
                    axisLine={false}
                    padding={{ left: 0, right: 0 }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, domain.yMax]}
                    ticks={domain.ticks}
                    tick={false}
                    axisLine={false}
                    tickLine={false}
                    width={Y_AXIS_WIDTH_PX}
                    tickMargin={6}
                  />
                  {outlierDots.length > 0 ? (
                    <Customized
                      component={
                        <OutlierDotsLayer
                          dots={outlierDots}
                          categoryCount={data.length}
                          innerColor={WHISKER_COLOR}
                        />
                      }
                    />
                  ) : null}
                  {hlOutlierDots.length > 0 ? (
                    <Customized
                      component={
                        <OutlierDotsLayer
                          dots={hlOutlierDots}
                          categoryCount={data.length}
                          innerColor={BOX_STROKE_HIGHLIGHT}
                        />
                      }
                    />
                  ) : null}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div
      className="cement-boxplot-container"
      style={{ "--cement-plot-left": `${PLOT_LEFT_OFFSET_PX}px` } as React.CSSProperties}
    >
      {/* Title Section */}
      <header className="cement-boxplot-header" />

      {/* Filter Controls */}
      {showFilters && (
        <div className="cement-boxplot-filters">
          {/* Metric (dropdown) */}
          <div className="cement-filter-row">
            <span className="cement-filter-label">Metric</span>
            <div className="cement-filter-options">
              <select
                className="cement-select"
                value={metric}
                onChange={(e) => handleMetricChange(e.target.value as Metric)}
                aria-label={`Select ${metricAria(metric)} metric`}
              >
                <option value="carbon">Embodied Carbon</option>
                <option value="energy">Embodied Energy</option>
              </select>
            </div>
          </div>

          {/* Material Sub-type: Yes = each sub-material gets its own block of date columns */}
          <div
            className="cement-filter-row"
            title='Yes: one block per sub-material (e.g. Fly Ash black), each with all time-slicer date categories.'
          >
            <span className="cement-filter-label">Material Sub-type</span>
            <div className="cement-filter-options">
              {(["Yes", "No"] as const).map((opt) => (
                <label key={`submat-${opt}`} className="cement-radio-wrapper">
                  <input
                    type="radio"
                    name="materialSubType"
                    checked={filters.materialSubType === opt}
                    onChange={() => setFilters((prev) => ({ ...prev, materialSubType: opt }))}
                  />
                  <span className="cement-radio-circle" />
                  <span className="cement-radio-text">{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Manufacturing Technology */}
          <div
            className="cement-filter-row"
            title="Yes: split columns by manufacturing technology within each sub-material group."
          >
            <span className="cement-filter-label">Manufacturing Technology</span>
            <div className="cement-filter-options">
              {(["Yes", "No"] as const).map((opt) => (
                <label key={`mantech-${opt}`} className="cement-radio-wrapper">
                  <input
                    type="radio"
                    name="manufacturingTechnology"
                    checked={filters.manufacturingTechnology === opt}
                    onChange={() => setFilters((prev) => ({ ...prev, manufacturingTechnology: opt }))}
                  />
                  <span className="cement-radio-circle" />
                  <span className="cement-radio-text">{opt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Time Slicer */}
          <div className="cement-filter-row">
            <span className="cement-filter-label">Time slicer</span>
            <div className="cement-filter-options">
              {(["10 Years", "5 Years", "1 Year", "6 Months", "None"] as const).map((opt) => (
                <label key={`time-${opt}`} className="cement-radio-wrapper">
                  <input
                    type="radio"
                    name="timeSlicer"
                    checked={filters.timeSlicer === opt}
                    onChange={() => setFilters((prev) => ({ ...prev, timeSlicer: opt }))}
                  />
                  <span className="cement-radio-circle" />
                  <span className="cement-radio-text">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SVG Charts */}
      {renderChart(metric)}

      {showHighlightLegend && charts.trackHighlight ? (
        <div className="cement-boxplot-highlight-legend" aria-label="Box plot legend">
          <span className="cement-legend-item">
            <span className="cement-legend-swatch cement-legend-swatch--all" aria-hidden />
            All datasets
          </span>
          <span className="cement-legend-item">
            <span className="cement-legend-swatch cement-legend-swatch--review" aria-hidden />
            Flagged dataset
          </span>
        </div>
      ) : null}
    </div>
  );
};

/** React component — Cement Box Plot Chart. */
export default CementBoxPlotChart;
